import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Table, Spinner, Badge } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

// --- helpers (mirroring ETAReport style) ---
const toDate = (ts) =>
  ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : (ts ? new Date(ts) : null));

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

const fmtMDY = (d) => d ? `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}` : '';

const formatYYYYMMDD = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDateInput = (s) => { const [y, m, d] = (s || '').split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1); };

const capitalizeFirstLetter = (s = '') => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');

const resolveModelName = (vehicle, modelCodes = {}) => {
  const factory = (vehicle?.strFactory ?? '').trim();
  const model = (vehicle?.strModel ?? '').trim();
  const make = (vehicle?.strMake ?? '').trim();
  if (!factory && !model) return model || '';

  const exactKey = `${factory} - ${model}`;
  const shortKey = `${factory} - ${model.substring(0, 3)}`;
  const makeShortKey = `${capitalizeFirstLetter(make)} - ${model.substring(0, 3)}`;
  const gmShortKey = `GM - ${model.substring(0, 3)}`;

  return (
    modelCodes[exactKey] ||
    modelCodes[shortKey] ||
    modelCodes[makeShortKey] ||
    modelCodes[gmShortKey] ||
    model
  );
};

// --- component ---
export default function PaidReport({ defaultDeliveredOnly = true }) {
  const { onSnapshotData, queryData } = useFirestore();

  const [vehicles, setVehicles] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [upfitterStatusCodes, setUpfitterStatusCodes] = useState([]);
  const [modelCodes, setModelCodes] = useState({});
  const [vehiclesReady, setVehiclesReady] = useState(false);
  const [salesOrdersReady, setSalesOrdersReady] = useState(false);
  const [customersReady, setCustomersReady] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const [upfitterReady, setUpfitterReady] = useState(false);

  const loading = useMemo(() => !(vehiclesReady && salesOrdersReady && customersReady && modelsReady && upfitterReady), [vehiclesReady, salesOrdersReady, customersReady, modelsReady, upfitterReady]);

  const [deliveredOnly, setDeliveredOnly] = useState(!!defaultDeliveredOnly);
  const [paidFrom, setPaidFrom] = useState(null); // date object or null

  // live data
  useEffect(() => {
    const uV = onSnapshotData('vehicles', (v) => { setVehicles(v || []); setVehiclesReady(true); });
    const uS = onSnapshotData('salesOrders', (s) => { setSalesOrders(s || []); setSalesOrdersReady(true); });
    const uC = onSnapshotData('customers', (c) => { setCustomers(c || []); setCustomersReady(true); });
    const uM = onSnapshotData('modelCodes', (mc = []) => {
      const map = (mc || []).reduce((acc, obj) => {
        acc[`${obj.strFactory} - ${obj.strModel}`] = obj.strModelName;
        return acc;
      }, {});
      setModelCodes(map);
      setModelsReady(true);
    });
    return () => { uV && uV(); uS && uS(); uC && uC(); uM && uM(); };
  }, [onSnapshotData]);

  // one-time fetch for upfitter status code names
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const codes = await queryData('eventCodes', 'strFactory', '==', 'UPFITTER');
        if (mounted) setUpfitterStatusCodes(codes || []);
      } catch (e) {
        console.warn('Failed to load upfitter status codes', e);
      } finally {
        if (mounted) setUpfitterReady(true);
      }
    })();
    return () => { mounted = false; };
  }, [queryData]);

  // lookups
  const soByNbr = useMemo(() => {
    const m = new Map();
    (salesOrders || []).forEach(so => m.set(so.strSalesOrdNbr, so));
    return m;
  }, [salesOrders]);

  const custByNbr = useMemo(() => {
    const m = new Map();
    (customers || []).forEach(c => m.set(c.strCustNbr, c));
    return m;
  }, [customers]);

  const paidFromFloor = useMemo(() => (paidFrom ? startOfDay(paidFrom) : null), [paidFrom]);

  // helpers from VehicleList --------------------------------------------
  const getUpfitterStatus = (strUpfitterStatus) => {
    if (strUpfitterStatus == null || strUpfitterStatus === '') return null;
    const key = String(strUpfitterStatus);
    const code = upfitterStatusCodes.find(c => String(c?.strManStat) === key);
    return code ? code.strFactStatus : null;
  };

  const getDisplayedStatus = (vehicle) => {
    // Manual overrides
    if (vehicle.manualStatus === 'Scheduled') return 'Scheduled';
    if (vehicle.manualStatus === 'Produced') return 'Produced';
    if (vehicle.manualStatus === 'In Transit') return 'In Transit';
    if (vehicle.manualStatus === 'On the Ground') return 'On the Ground';
    if (vehicle.manualStatus === 'Customer Signed') return 'Customer Signed';
    if (vehicle.manualStatus === 'At Upfitter') {
      if (vehicle.strUpfitterStatus && vehicle.strUpfitterStatus !== 'Complete') {
        return getUpfitterStatus(vehicle.strUpfitterStatus) || 'At Upfitter';
      }
      return 'At Upfitter';
    }
    if (vehicle.manualStatus === 'Delivered') return 'Delivered';
    if (vehicle.manualStatus === 'Complete') return 'Complete';

    // Process of getting vehicle ready
    if (vehicle.dtCustSignedDate) return 'Customer Signed and Ownership Changed';

    if (vehicle.strUpfitterStatus && vehicle.strUpfitterStatus !== 'Complete') {
      return getUpfitterStatus(vehicle.strUpfitterStatus) || 'At Upfitter';
    }

    // Ready for customer signature
    if (vehicle.dtRecvDate) return 'On the Ground';

    if (vehicle.strUpfitterStatus && vehicle.strUpfitterStatus === 'Complete') return 'Delivered to Customer';

    // In-process
    if (vehicle.strManStat || vehicle.dtProdDate || vehicle.dtManETA) {
      if (vehicle.dtManETA) return 'In Transit';
      if (vehicle.strManStat && (parseInt(vehicle.strManStat) >= 3800 || vehicle.strManStat === 'Produced')) return 'Produced';
      if (vehicle.dtProdDate) return 'Scheduled';
    }

    // Ordered
    if (!vehicle.strManStat && !vehicle.dtProdDate && !vehicle.dtManETA) return 'Vehicle Ordered';

    return 'Vehicle Ordered';
  };

  // rows
  const rows = useMemo(() => {
    const base = (vehicles || []).filter(v => !!v?.dtPaidDate);

    const delivered = deliveredOnly
      ? base.filter(v => (v?.manualStatus || '').trim() === 'Delivered')
      : base;

    const ranged = paidFromFloor
      ? delivered.filter(v => { const d = toDate(v.dtPaidDate); return d && d >= paidFromFloor; })
      : delivered;

    // newest paid first
    return [...ranged].sort((a, b) => {
      const da = toDate(a.dtPaidDate) ? toDate(a.dtPaidDate).getTime() : 0;
      const db = toDate(b.dtPaidDate) ? toDate(b.dtPaidDate).getTime() : 0;
      return db - da;
    });
  }, [vehicles, deliveredOnly, paidFromFloor]);

  // header stats
  const lastPaidOverall = useMemo(() => {
    const ds = (vehicles || []).map(v => toDate(v.dtPaidDate)).filter(Boolean);
    if (ds.length === 0) return null;
    return new Date(Math.max(...ds.map(d => d.getTime())));
  }, [vehicles]);

  const today = new Date();

  return (
    <Card>
      <Card.Body>
        {/* Header like ETAReport */}
        <div className="text-center mb-3">
          <div className="fw-bold">Hardy Automotive Fleet Group</div>
          <div>Paid Vehicles</div>
          <div className="small text-muted">{lastPaidOverall ? `Last Paid Date — ${fmtMDY(lastPaidOverall)}` : 'No paid vehicles yet'}</div>
          <div className="small">As of {fmtMDY(today)}</div>
        </div>

        {/* Controls */}
        <Row className="g-3 align-items-end mb-3">
          <Col xs={12} md={3}>
            <Form.Check
              type="switch"
              id="delivered-only"
              label="Delivered Only"
              checked={deliveredOnly}
              onChange={(e) => setDeliveredOnly(e.target.checked)}
            />
          </Col>
          <Col xs={12} md={3}>
            <Form.Label>Paid on or after</Form.Label>
            <Form.Control
              type="date"
              value={paidFrom ? formatYYYYMMDD(paidFrom) : ''}
              onChange={(e) => setPaidFrom(e.target.value ? parseDateInput(e.target.value) : null)}
            />
          </Col>
          <Col xs={12} md={6} className="text-md-end">
            <div className="mt-2 mt-md-0">
              <Badge bg="secondary">{rows.length} vehicle{rows.length === 1 ? '' : 's'}</Badge>
              {paidFrom && <span className="ms-2 small text-muted">From {fmtMDY(paidFrom)}</span>}
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
            <Spinner animation="border" />
          </div>
        ) : (
          <Table bordered responsive className="align-middle">
            <thead>
              <tr>
                <th>Sales Order</th>
                <th>Stock #</th>
                <th>VIN</th>
                <th>Customer</th>
                <th className="text-center">Yr</th>
                <th>Make</th>
                <th>Model</th>
                <th>Status</th>
                <th className="text-nowrap">Paid Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">No results</td>
                </tr>
              ) : rows.map(v => {
                const so = soByNbr.get(v.strSalesOrdNbr);
                const customer = so ? (custByNbr.get(so.strCustNbr)?.strCustName || '') : '';
                const paid = toDate(v.dtPaidDate);
                return (
                  <tr key={v.id}>
                    <td>{v.strSalesOrdNbr || ''}</td>
                    <td>{v.strStockNbr || (v.strVin ? v.strVin.slice(-8) : '')}</td>
                    <td style={{ fontFamily: 'monospace' }}>{v.strVin || ''}</td>
                    <td>{customer}</td>
                    <td className="text-center">{v.strYR || ''}</td>
                    <td>{v.strMake || ''}</td>
                    <td>{resolveModelName(v, modelCodes)}</td>
                    <td>{getDisplayedStatus(v)}</td>
                    <td>{fmtMDY(paid)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
